import { Module } from 'vuex';
import { HealthReportsActions } from '@/modules/reports/health-reports/store/health-reports.actions';
import { Exception } from '@/modules/shared/shared/domain/exceptions/exception';
import healthReportsRepository from '@/modules/reports/shared/repositories/health-reports.repository';
import { UserGetters } from '@/modules/users/store/user.getters';
import { HealthReportsState } from '@/modules/reports/health-reports/store/health-reports.state';
import { HealthReportsMutations } from '@/modules/reports/health-reports/store/health-reports.mutations';
import { HealthReportFile } from '@/modules/reports/health-reports/domain/health-report-file';
import { HealthReport } from '@/modules/reports/health-reports/domain/health-report';
import { HealthReportsGetters } from '@/modules/reports/health-reports/store/health-reports.getters';
import { HealthReportManagementView } from '@/modules/reports/health-reports/domain/health-report-management-view';
import customersRepository from '@/modules/customers/repositories/customers.repository';
import { HealthReportUpload } from '@/modules/reports/health-reports/domain/health-report-upload';
import moment from 'moment/moment';
import { EventBus } from '@/main';
import { EventBusMessages } from '@/modules/shared/shared/domain/constants/event-bus-messages';

const healthReports: Module<HealthReportsState, any> = {
    namespaced: true,
    state: {
        healthReportsView: {
            reports: [],
        },
        healthReportManagementView: {
            customers: {
                start: 0,
                limit: 0,
                total: 0,
                pages: 0,
                results: [],
            },
            search: '',
        },
    },
    getters: {
        [HealthReportsGetters.GET_AVAILABLE_REPORTS](state): HealthReport[] {
            return state.healthReportsView.reports;
        },
        [HealthReportsGetters.GET_MANAGEMENT_VIEW](state): HealthReportManagementView {
            return state.healthReportManagementView;
        },
    },
    mutations: {
        [HealthReportsMutations.SET_REPORTS_VIEW](state, reports: HealthReportFile[]) {
            state.healthReportsView.reports = reports
                .map(t => {
                    return new HealthReport(t);
                })
                .sort((a, b) => b.ord - a.ord)
                .slice(0, 4)
                .reverse();
        },
        [HealthReportsMutations.SET_MANAGEMENT_VIEW](state, view: HealthReportManagementView) {
            state.healthReportManagementView = view;
        },
    },
    actions: {
        async [HealthReportsActions.FETCH_REPORTS](
            { commit, rootGetters, state },
            customerId: number,
        ): Promise<RequestResponse> {
            try {
                if (customerId === null)
                    customerId = rootGetters[`user/${UserGetters.GET_CUSTOMER_ID}`];
                const reports = await healthReportsRepository.getAll(customerId);
                commit(HealthReportsMutations.SET_REPORTS_VIEW, reports);
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },
        async [HealthReportsActions.DOWNLOAD_REPORT](
            {},
            report: HealthReport,
        ): Promise<RequestResponse> {
            try {
                const response = await healthReportsRepository.getReport(report);
                const file = new Blob([response.data], {
                    type: 'application/pdf',
                });
                const fileURL = URL.createObjectURL(file);
                window.open(fileURL);
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },

        async [HealthReportsActions.FETCH_CUSTOMERS]({ commit }): Promise<RequestResponse> {
            try {
                commit(HealthReportsMutations.SET_MANAGEMENT_VIEW, {
                    customers: {
                        start: 0,
                        limit: 0,
                        total: 0,
                        pages: 0,
                        results: [],
                    },
                    search: '',
                });
                const customers = await customersRepository.getCustomers({
                    limit: 10,
                    start: 1,
                    keyword: '',
                    cav: true,
                });
                commit(HealthReportsMutations.SET_MANAGEMENT_VIEW, {
                    customers,
                    search: '',
                });
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },

        async [HealthReportsActions.SEARCH_CUSTOMER]({ commit, state }, search: string) {
            try {
                const customers = await customersRepository.getCustomers({
                    limit: state.healthReportManagementView.customers.limit,
                    start: 1,
                    keyword: search,
                    cav: search.length === 0,
                });
                commit(HealthReportsMutations.SET_MANAGEMENT_VIEW, {
                    customers,
                    search,
                });
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },

        async [HealthReportsActions.CHANGE_PAGE]({ commit, state }, { page, limit }) {
            try {
                const customers = await customersRepository.getCustomers({
                    limit: limit || 10,
                    start: page || 1,
                    keyword: state.healthReportManagementView.search,
                    cav: state.healthReportManagementView.search.length === 0,
                });
                commit(HealthReportsMutations.SET_MANAGEMENT_VIEW, {
                    customers,
                    search: state.healthReportManagementView.search,
                });
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },

        async [HealthReportsActions.UPLOAD_REPORT](
            { commit },
            report: HealthReportUpload,
        ): Promise<RequestResponse> {
            try {
                let formData = new FormData();
                formData.set('file', report.file, report.file.name);
                const file = await healthReportsRepository.uploadFile({
                    customerId: report.customerId,
                    date: moment()
                        .month(report.month - 1)
                        .year(report.year)
                        .format('YYYY-MM'),
                    formData,
                });
                EventBus.$emit(
                    EventBusMessages.SHOW_SUCCESS,
                    `Se ha cargado el archivo exitosamente (Nombre del archivo: ${file.filename})`,
                );
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },
    },
};

export default healthReports;
